import React, { useContext } from 'react'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { useDispatch } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { BottomSheet } from '@src/components'
import { Text } from '@src/components/base'
import { StyleService, useStyleSheet } from '@src/style/service'
import { AppStackParamList } from '@src/navigation/types'
import { useDispatchAsync } from '@src/utils/dispatch'
import { NavigatorContext, useSnack } from '@src/utils/navigatorContext'
import { ErrorMessages } from '@src/config'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { EhrAppointmentQuery } from '../graphql/ehrAppointment.generated'
import { isAppointmentCancelled } from '../utils'

export const RescheduleOrCancelVideoCallModal = () => {
  const styles = useStyleSheet(themedStyles)
  const dispatch = useDispatch()
  const dispatchAsync = useDispatchAsync()
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const showSnack = useSnack()
  const route = useRoute<RouteProp<AppStackParamList, 'RescheduleOrCancelVideoCallModal'>>()

  const {
    videoCallId,
    appointmentKind,
    nutritionistName,
    recurringAppointment,
    appointmentDate,
  } = route.params

  useLazyScreenPreload('CancelRecurringAppointmentModal')

  const onReschedulePress = () => {
    navigation.navigate('CallScheduling', {
      appointmentKind,
      appointmentId: videoCallId,
      allowBackNavigation: true,
    })
  }

  const navigateToNutritionistHub = () => {
    navigation.replace('Drawer', { screen: 'Dashboard', params: { screen: 'NutritionistHub' } })
  }

  const onCancelPress = () => {
    if (recurringAppointment) {
      navigation.navigate('CancelRecurringAppointmentModal', {
        videoCallId,
        appointmentDate,
      })

      return
    }

    enableLoader()
    dispatch({
      type: 'nutritionistHub/cancelAppointment',
      payload: {
        id: videoCallId,
      },
      success: () => {
        navigateToNutritionistHub()
      },
      failure: async (error: any) => {
        try {
          const appointment = await dispatchAsync<EhrAppointmentQuery['ehrAppointment']>({
            type: 'nutritionistHub/fetchEhrAppointment',
            payload: { id: videoCallId },
          })

          if (isAppointmentCancelled(appointment)) {
            navigateToNutritionistHub()
            return
          }
        } catch {
          // If the follow-up check fails, fall back to the original mutation error.
        }

        const errorMessage = error?.message || ErrorMessages.ServerError
        showSnack(errorMessage, null, 'error', 'x', undefined, 'redesigned')
      },
      complete: () => {
        disableLoader()
      },
    })
  }

  const effectiveNutritionistName = nutritionistName || 'Nutritionist'

  return (
    <BottomSheet
      primaryButton={{ text: 'Reschedule', onPress: onReschedulePress }}
      secondaryButton={{ text: 'Cancel Appointment', onPress: onCancelPress }}
      buttonDirection="column-reverse"
      showDismissButton
      dismissButtonPosition="top"
    >
      <Text style={styles.title} type="title-2">
        Would you like to reschedule instead of cancelling?
      </Text>
      <Text style={styles.content} type="regular">
        Life happens! Opt to reschedule instead of canceling to receive personalized guidance from{' '}
        {effectiveNutritionistName} and reach your health goals.
      </Text>
    </BottomSheet>
  )
}

const themedStyles = StyleService.create({
  title: {
    textAlign: 'center',
    paddingTop: 16,
    paddingBottom: 16,
  },
  content: {
    textAlign: 'center',
  },
  nutritionistPhoto: {
    alignSelf: 'center',
  },
})
